import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './FlowBuilderCanvas.css';

// ========== ALLE TOUCHPOINT OPTIONS ==========
const TOUCHPOINT_OPTIONS = {
  // Meetings & Calls
  meet_in_person: { label: "Meet In Person", icon: "fa-users", points: 4, category: "Meetings & Calls" },
  online_meeting: { label: "Online Meeting", icon: "fa-video", points: 3, category: "Meetings & Calls" },
  phone_call: { label: "Phone Call", icon: "fa-phone-alt", points: 2, category: "Meetings & Calls" },
  // Legacy meeting/call types — kept so already-saved flows keep rendering, but no
  // longer offered as new options in the builder palette or edit dropdown.
  sales_meeting: { label: "Sales Meeting", icon: "fa-handshake", points: 4, category: "Meetings & Calls", legacy: true },
  discovery_call: { label: "Discovery Call", icon: "fa-search", points: 3, category: "Meetings & Calls", legacy: true },
  follow_up_call: { label: "Follow Up Call", icon: "fa-phone-volume", points: 2, category: "Meetings & Calls", legacy: true },
  
  // Sales Process
  demo_given: { label: "Demo Given", icon: "fa-play-circle", points: 3, category: "Sales Process" },
  proposal_sent: { label: "Proposal Sent", icon: "fa-file-alt", points: 3, category: "Sales Process" },
  quote_sent: { label: "Quote Sent", icon: "fa-file-invoice-dollar", points: 2, category: "Sales Process" },
  contract_sent: { label: "Contract Sent", icon: "fa-file-contract", points: 3, category: "Sales Process" },
  contract_signed: { label: "Contract Signed", icon: "fa-file-signature", points: 5, category: "Sales Process" },
  invoice_sent: { label: "Invoice Sent", icon: "fa-file-invoice", points: 2, category: "Sales Process" },
  payment_received: { label: "Payment Received", icon: "fa-money-bill-wave", points: 5, category: "Sales Process" },
  onboarding: { label: "Onboarding", icon: "fa-rocket", points: 4, category: "Sales Process" },
  
  // Email
  email_sent: { label: "Send Email", icon: "fa-paper-plane", points: 1, category: "Email" },
  newsletter: { label: "Send Newsletter", icon: "fa-newspaper", points: 0.5, category: "Email" },
  // Legacy email types — kept so already-saved flows keep rendering, but no
  // longer offered as new options in the builder palette.
  cold_email: { label: "Cold Email", icon: "fa-snowflake", points: 1, category: "Email", legacy: true },
  follow_up_email: { label: "Follow Up Email", icon: "fa-reply", points: 1, category: "Email", legacy: true },
  nurture_email: { label: "Nurture Email", icon: "fa-heart", points: 1, category: "Email", legacy: true },
  case_study_email: { label: "Case Study Email", icon: "fa-chart-bar", points: 1.5, category: "Email", legacy: true },
  testimonial_email: { label: "Testimonial Email", icon: "fa-star", points: 1.5, category: "Email", legacy: true },
  reminder_email: { label: "Reminder Email", icon: "fa-bell", points: 1, category: "Email", legacy: true },
  
  // Messaging
  whatsapp_message: { label: "WhatsApp", icon: "fa-whatsapp", isBrand: true, points: 1.5, category: "Messaging" },
  sms_sent: { label: "SMS", icon: "fa-sms", points: 1, category: "Messaging" },
  voice_message: { label: "Voice Message", icon: "fa-microphone", points: 1.5, category: "Messaging" },
  loom_video: { label: "Loom Video", icon: "fa-video", points: 2, category: "Messaging" },
  video_message: { label: "Video Message", icon: "fa-film", points: 2, category: "Messaging" },
  slack_message: { label: "Slack Message", icon: "fa-slack", isBrand: true, points: 1, category: "Messaging" },
  website_form: { label: "Website Contact Form", icon: "fa-wpforms", isBrand: true, points: 1.5, category: "Messaging" },
  
  // Social Media
  linkedin_profile_visit: { label: "LinkedIn Profile Visit", icon: "fa-linkedin", isBrand: true, points: 0.25, category: "Social Media" },
  linkedin_connect: { label: "LinkedIn Connect", icon: "fa-linkedin", isBrand: true, points: 0.5, category: "Social Media" },
  linkedin_message: { label: "LinkedIn DM", icon: "fa-linkedin", isBrand: true, points: 1.5, category: "Social Media" },
  linkedin_comment: { label: "LinkedIn Comment", icon: "fa-comment", points: 0.5, category: "Social Media" },
  linkedin_like: { label: "LinkedIn Like", icon: "fa-thumbs-up", points: 0.25, category: "Social Media" },
  instagram_follow: { label: "Instagram Follow", icon: "fa-instagram", isBrand: true, points: 0.5, category: "Social Media" },
  instagram_interaction: { label: "Instagram Interaction", icon: "fa-instagram", isBrand: true, points: 0.5, category: "Social Media" },
  instagram_dm: { label: "Instagram DM", icon: "fa-instagram", isBrand: true, points: 1.5, category: "Social Media" },
  instagram_comment: { label: "Instagram Comment", icon: "fa-comment", points: 0.5, category: "Social Media" },
  twitter_follow: { label: "Twitter/X Follow", icon: "fa-twitter", isBrand: true, points: 0.5, category: "Social Media" },
  twitter_dm: { label: "Twitter/X DM", icon: "fa-twitter", isBrand: true, points: 1.5, category: "Social Media" },
  facebook_message: { label: "Facebook Message", icon: "fa-facebook", isBrand: true, points: 1, category: "Social Media" },
  
  // Content & Resources
  lead_magnet: { label: "Lead Magnet", icon: "fa-magnet", points: 1, category: "Content" },
  ebook_sent: { label: "eBook Sent", icon: "fa-book", points: 1.5, category: "Content" },
  whitepaper_sent: { label: "Whitepaper Sent", icon: "fa-file-pdf", points: 1.5, category: "Content" },
  case_study_sent: { label: "Case Study Sent", icon: "fa-chart-line", points: 2, category: "Content" },
  webinar: { label: "Webinar Invite", icon: "fa-desktop", points: 2, category: "Content" },
  workshop_invite: { label: "Workshop Invite", icon: "fa-chalkboard-teacher", points: 2.5, category: "Content" },
  free_trial: { label: "Free Trial Offered", icon: "fa-gift", points: 3, category: "Content" },
  discount_offer: { label: "Discount Offer", icon: "fa-percent", points: 2, category: "Content" },
  
  // Tasks & Internal
  follow_up: { label: "Follow Up Task", icon: "fa-redo", points: 1, category: "Tasks" },
  research_lead: { label: "Research Lead", icon: "fa-search", points: 1, category: "Tasks" },
  update_crm: { label: "Update CRM", icon: "fa-database", points: 0.5, category: "Tasks" },
  assign_to_rep: { label: "Assign to Rep", icon: "fa-user-plus", points: 1, category: "Tasks" },
  add_note: { label: "Add Note", icon: "fa-sticky-note", points: 0.5, category: "Tasks" },
  add_tag: { label: "Add Tag", icon: "fa-tag", points: 0.25, category: "Tasks" },
  move_stage: { label: "Move Stage", icon: "fa-arrows-alt-h", points: 1, category: "Tasks" },
  create_task: { label: "Create Task", icon: "fa-check-square", points: 0.5, category: "Tasks" },
};

// ========== CUSTOM NODE: TRIGGER ==========
const TriggerNode = ({ data }) => {
  return (
    <div className="flowNode triggerNode">
      <div className="nodeIcon">
        <i className="fa fa-play"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Start</span>
        <span className="nodeSubLabel">{data.triggerLabel || 'When Lead Created'}</span>
      </div>
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: ACTION ==========
const ActionNode = ({ data, id }) => {
  const tp = TOUCHPOINT_OPTIONS[data.actionType] || { label: 'Action', icon: 'fa-circle' };
  
  return (
    <div className="flowNode actionNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon actionIcon">
        <i className={`${tp.isBrand ? 'fab' : 'fa'} ${tp.icon}`}></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">{tp.label}</span>
        {data.actionType === 'email_sent' && data.templateName && (
          <span className="nodeNote"><i className="fa fa-file-alt"></i> {data.templateName}</span>
        )}
        {data.note && <span className="nodeNote">{data.note}</span>}
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: SPLIT (A/B Test) ==========
const SplitNode = ({ data, id }) => {
  return (
    <div className="flowNode splitNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon splitIcon">
        <i className="fa fa-code-branch"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">A/B Split</span>
        <div className="splitRatio">
          <span className="splitA">{data.splitA || 50}% A</span>
          <span className="splitB">{data.splitB || 50}% B</span>
        </div>
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-sliders-h"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="a" 
        className="nodeHandle splitHandleA"
        style={{ left: '30%' }}
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="b" 
        className="nodeHandle splitHandleB"
        style={{ left: '70%' }}
      />
      <div className="splitLabels">
        <span className="splitLabelA">A</span>
        <span className="splitLabelB">B</span>
      </div>
    </div>
  );
};

// ========== CUSTOM NODE: DELAY ==========
const DelayNode = ({ data, id }) => {
  return (
    <div className="flowNode delayNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon delayIcon">
        <i className="fa fa-hourglass-half"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Wait</span>
        <span className="nodeSubLabel">{data.delayDays || 1} days</span>
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== END TYPE CONFIG ==========
const END_TYPE_CONFIG = {
  completed: { label: 'Completed', icon: 'fa-check-circle', color: '#22c55e' },
  converted: { label: 'Converted', icon: 'fa-trophy', color: '#f59e0b' },
  disqualified: { label: 'Disqualified', icon: 'fa-ban', color: '#ef4444' },
  not_interested: { label: 'Not Interested', icon: 'fa-times-circle', color: '#f97316' },
  wrong_contact: { label: 'Wrong Contact', icon: 'fa-exclamation-triangle', color: '#6b7280' },
  nurture: { label: 'Nurture', icon: 'fa-clock', color: '#3b82f6' },
};

// ========== CUSTOM NODE: END ==========
const EndNode = ({ data, id }) => {
  const endType = data.endType || 'completed';
  const config = END_TYPE_CONFIG[endType] || END_TYPE_CONFIG.completed;
  
  return (
    <div className={`flowNode endNode endType-${endType}`}>
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon endIcon" style={{ backgroundColor: `${config.color}20`, color: config.color }}>
        <i className={`fa ${config.icon}`}></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">End</span>
        <span className="nodeSubLabel" style={{ color: config.color }}>{config.label}</span>
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
    </div>
  );
};

// ========== CONDITION OPTIONS ==========
const CONDITION_OPTIONS = {
  // Automatic Conditions
  is_favorite: { label: 'Is Favorite', icon: 'fa-heart', type: 'auto' },
  email_opened: { label: 'Email Opened', icon: 'fa-envelope-open', type: 'auto' },
  email_clicked: { label: 'Email Clicked', icon: 'fa-mouse-pointer', type: 'auto' },
  replied: { label: 'Replied', icon: 'fa-reply', type: 'auto' },
  score_above: { label: 'Score Above Threshold', icon: 'fa-chart-line', type: 'auto' },
  has_phone: { label: 'Has Phone Number', icon: 'fa-phone', type: 'auto' },
  has_email: { label: 'Has Email', icon: 'fa-at', type: 'auto' },
  has_company: { label: 'Has Company', icon: 'fa-building', type: 'auto' },
  
  // Manual Decision Conditions
  shown_interest: { label: 'Has Shown Interest?', icon: 'fa-heart', type: 'manual' },
  qualified: { label: 'Is Qualified?', icon: 'fa-check-circle', type: 'manual' },
  decision_maker: { label: 'Is Decision Maker?', icon: 'fa-user-tie', type: 'manual' },
  has_budget: { label: 'Has Budget?', icon: 'fa-dollar-sign', type: 'manual' },
  good_fit: { label: 'Good Fit?', icon: 'fa-puzzle-piece', type: 'manual' },
  ready_to_buy: { label: 'Ready to Buy?', icon: 'fa-shopping-cart', type: 'manual' },
  needs_follow_up: { label: 'Needs Follow Up?', icon: 'fa-redo', type: 'manual' },
  meeting_went_well: { label: 'Meeting Went Well?', icon: 'fa-thumbs-up', type: 'manual' },
  responded_positively: { label: 'Responded Positively?', icon: 'fa-smile', type: 'manual', analytics: true },
  custom_question: { label: 'Custom Question', icon: 'fa-question-circle', type: 'manual' },

  // Analytics-Aware Conditions (manual + auto-log to sales analytics)
  meeting_booked: { label: 'Meeting Booked?', icon: 'fa-calendar-check', type: 'manual', analytics: true },
  deal_closed: { label: 'Deal Closed?', icon: 'fa-handshake', type: 'manual', analytics: true },
};

// ========== CUSTOM NODE: CONDITION ==========
const ConditionNode = ({ data, id }) => {
  const condition = CONDITION_OPTIONS[data.condition] || { label: data.condition || 'If...', type: 'auto' };
  const isManual = condition.type === 'manual';
  
  return (
    <div className={`flowNode conditionNode ${isManual ? 'manualCondition' : ''}`}>
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon conditionIcon">
        <i className={`fa ${isManual ? 'fa-user-check' : 'fa-cog'}`}></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">
          {isManual ? 'Decision' : 'Condition'}
        </span>
        <span className="nodeSubLabel">
          {data.customQuestion || condition.label}
        </span>
        {isManual && (
          <span className="nodeManualBadge">
            <i className="fa fa-hand-pointer"></i> Manual
          </span>
        )}
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="yes" 
        className="nodeHandle conditionHandleYes"
        style={{ left: '30%' }}
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="no" 
        className="nodeHandle conditionHandleNo"
        style={{ left: '70%' }}
      />
      <div className="conditionLabels">
        <span className="conditionLabelYes">{isManual ? '✓' : 'Yes'}</span>
        <span className="conditionLabelNo">{isManual ? '✗' : 'No'}</span>
      </div>
    </div>
  );
};

// ========== PREDEFINED TAGS ==========
const PREDEFINED_TAGS = [
  { id: 'dream_client', name: 'Dream Client', color: '#8b5cf6', icon: 'fa-star' },
  { id: 'hot_lead', name: 'Hot Lead', color: '#ef4444', icon: 'fa-fire' },
  { id: 'warm_lead', name: 'Warm Lead', color: '#f59e0b', icon: 'fa-temperature-half' },
  { id: 'cold_lead', name: 'Cold Lead', color: '#3b82f6', icon: 'fa-snowflake' },
  { id: 'qualified', name: 'Qualified', color: '#22c55e', icon: 'fa-check-circle' },
  { id: 'not_qualified', name: 'Not Qualified', color: '#6b7280', icon: 'fa-times-circle' },
  { id: 'decision_maker', name: 'Decision Maker', color: '#ec4899', icon: 'fa-user-tie' },
  { id: 'influencer', name: 'Influencer', color: '#06b6d4', icon: 'fa-bullhorn' },
  { id: 'high_value', name: 'High Value', color: '#eab308', icon: 'fa-gem' },
  { id: 'high_budget', name: 'High Budget', color: '#22c55e', icon: 'fa-money-bill-wave' },
  { id: 'medium_budget', name: 'Medium Budget', color: '#f59e0b', icon: 'fa-money-bill' },
  { id: 'low_budget', name: 'Low Budget', color: '#ef4444', icon: 'fa-coins' },
  { id: 'priority', name: 'Priority', color: '#dc2626', icon: 'fa-exclamation-circle' },
  { id: 'follow_up_needed', name: 'Follow Up Needed', color: '#f97316', icon: 'fa-clock' },
  { id: 'nurture', name: 'Nurture', color: '#84cc16', icon: 'fa-seedling' },
  { id: 'engaged', name: 'Engaged', color: '#14b8a6', icon: 'fa-comments' },
  { id: 'unresponsive', name: 'Unresponsive', color: '#9ca3af', icon: 'fa-user-slash' },
  { id: 'interested', name: 'Interested', color: '#10b981', icon: 'fa-thumbs-up' },
  { id: 'not_interested', name: 'Not Interested', color: '#ef4444', icon: 'fa-thumbs-down' },
  { id: 'competitor', name: 'Competitor', color: '#7c3aed', icon: 'fa-chess' },
  { id: 'partner_potential', name: 'Partner Potential', color: '#0ea5e9', icon: 'fa-handshake' },
  { id: 'referral', name: 'Referral', color: '#d946ef', icon: 'fa-share' },
  { id: 'vip', name: 'VIP', color: '#fbbf24', icon: 'fa-crown' },
];

// ========== CUSTOM NODE: TAG ==========
const TagNode = ({ data, id }) => {
  const tags = data.tags || [];
  const displayTags = tags.slice(0, 3);
  const remainingCount = tags.length - 3;
  
  return (
    <div className="flowNode tagNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon tagIcon">
        <i className="fa fa-tags"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Add Tags</span>
        {tags.length > 0 ? (
          <div className="nodeTagsList">
            {displayTags.map((tag, idx) => (
              <span 
                key={idx} 
                className="nodeTagPill"
                style={{ backgroundColor: tag.color || '#6b7280' }}
              >
                {tag.icon && <i className={`fa ${tag.icon}`}></i>}
                {tag.name}
              </span>
            ))}
            {remainingCount > 0 && (
              <span className="nodeTagMore">+{remainingCount}</span>
            )}
          </div>
        ) : (
          <span className="nodeSubLabel">No tags selected</span>
        )}
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: DATA CAPTURE ==========
const DataCaptureNode = ({ data, id }) => {
  const fields = data.captureFields || [];
  const fieldLabels = { industry: 'Industry', size: 'Company Size', deal_value: 'Deal Value' };
  return (
    <div className="flowNode dataCaptureNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon" style={{ backgroundColor: '#7c3aed20', color: '#7c3aed' }}>
        <i className="fa fa-database"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Capture Data</span>
        <span className="nodeSubLabel">
          {fields.length > 0 ? fields.map(f => fieldLabels[f] || f).join(', ') : 'No fields selected'}
        </span>
      </div>
      {data.onEdit && (
        <div className="nodeActions">
          <button onClick={() => data.onEdit(id)} className="nodeEditBtn"><i className="fa fa-pen"></i></button>
          <button onClick={() => data.onDelete(id)} className="nodeDeleteBtn"><i className="fa fa-trash"></i></button>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: LOG NOTES ==========
// Manual, human-task step (like Capture Data): pauses the flow and shows a
// replica of the Notes composer so the user can log call/meeting notes to
// complete the step. Written to the contact's lead_notes array.
const LogNotesNode = ({ data, id }) => {
  return (
    <div className="flowNode logNotesNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon" style={{ backgroundColor: '#0ea5e920', color: '#0ea5e9' }}>
        <i className="fa fa-clipboard-list"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Log Notes</span>
        <span className="nodeSubLabel">
          {data.note ? data.note : 'Fill in call / meeting notes'}
        </span>
      </div>
      {data.onEdit && (
        <div className="nodeActions">
          <button onClick={() => data.onEdit(id)} className="nodeEditBtn"><i className="fa fa-pen"></i></button>
          <button onClick={() => data.onDelete(id)} className="nodeDeleteBtn"><i className="fa fa-trash"></i></button>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: AUTO SEND EMAIL ==========
const AutoSendEmailNode = ({ data, id }) => {
  const hasTemplate = !!data.templateId;
  const topicName = data.templateName || 'No template selected';
  const senderLabel = data.senderId === 'random' || !data.senderId
    ? 'Random Sender'
    : (data.senderName || data.senderId);
  
  return (
    <div className={`flowNode autoSendEmailNode ${!hasTemplate ? 'nodeIncomplete' : ''}`}>
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon autoSendEmailIcon">
        <i className="fa fa-paper-plane"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Auto Send Email</span>
        {!hasTemplate && (
          <span className="nodeWarning">
            <i className="fa fa-exclamation-triangle"></i> Setup required
          </span>
        )}
        {hasTemplate && (
          <>
            <span className="nodeSubLabel">{topicName}</span>
            <span className="nodeSubLabel nodeSubLabelSender">
              <i className="fa fa-user-circle"></i> {senderLabel}
            </span>
          </>
        )}
        <span className="nodeAutoBadge">
          <i className="fa fa-robot"></i> Automated
        </span>
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle type="source" position={Position.Bottom} className="nodeHandle" />
    </div>
  );
};

// ========== CUSTOM NODE: AUTO CHECK REPLY ==========
const AutoCheckReplyNode = ({ data, id }) => {
  const timeoutDays = data.timeoutDays || 3;
  
  return (
    <div className="flowNode autoCheckReplyNode">
      <Handle type="target" position={Position.Top} className="nodeHandle" />
      <div className="nodeIcon autoCheckReplyIcon">
        <i className="fa fa-envelope-open-text"></i>
      </div>
      <div className="nodeContent">
        <span className="nodeLabel">Check Reply</span>
        <span className="nodeSubLabel">Timeout: {timeoutDays}d</span>
        <span className="nodeAutoBadge">
          <i className="fa fa-robot"></i> Automated
        </span>
      </div>
      <div className="nodeActions">
        <button 
          className="nodeEditBtn" 
          onClick={(e) => { e.stopPropagation(); data.onEdit?.(id); }}
        >
          <i className="fa fa-pen"></i>
        </button>
        <button 
          className="nodeDeleteBtn"
          onClick={(e) => { e.stopPropagation(); data.onDelete?.(id); }}
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="yes" 
        className="nodeHandle conditionHandleYes"
        style={{ left: '30%' }}
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        id="no" 
        className="nodeHandle conditionHandleNo"
        style={{ left: '70%' }}
      />
      <div className="conditionLabels">
        <span className="conditionLabelYes">Reply</span>
        <span className="conditionLabelNo">No Reply</span>
      </div>
    </div>
  );
};

// ========== NODE TYPES (defined outside component to prevent re-renders) ==========
const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  split: SplitNode,
  delay: DelayNode,
  end: EndNode,
  condition: ConditionNode,
  tag: TagNode,
  data_capture: DataCaptureNode,
  log_notes: LogNotesNode,
  auto_send_email: AutoSendEmailNode,
  auto_check_reply: AutoCheckReplyNode,
};

// ========== DEFAULT EDGE OPTIONS (defined outside component) ==========
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
};

const snapGrid = [15, 15];

const NOOP = () => {};

// ========== FLOW TEMPLATES ==========
const createEdge = (sourceId, targetId, sourceHandle = null) => ({
  id: `e-${sourceId}-${targetId}${sourceHandle ? `-${sourceHandle}` : ''}`,
  source: sourceId,
  target: targetId,
  ...(sourceHandle ? { sourceHandle } : {}),
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#3b82f6', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
});

const FLOW_TEMPLATES = [
  // ===== WARMUP (COLD OUTREACH) TEMPLATES =====
  // All templates are value-first, multi-touch cold outreach cadences.
  // No newsletters — cold prospects have not given consent yet.
  {
    id: 'warmup_linkedin_value',
    name: 'LinkedIn-First Value Play',
    description: 'Warm up on LinkedIn, then lead with a free resource across email + phone.',
    icon: 'fa-user-plus',
    color: '#0a66c2',
    category: 'warmup',
    stepCount: 8,
    totalDays: 13,
    tags: ['LinkedIn', 'Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'linkedin_profile_visit', note: `Just view their profile so your name shows up first. No message yet.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 1 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'linkedin_connect', note: `Personalized request referencing a real signal (post, hire, launch). No pitch.` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 2 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'email_sent', note: `Value opener <120 words: name one pain + offer a free audit/benchmark. Give, do not ask.` } },
        { id: 'n6', type: 'delay', position: { x: 250, y: 850 }, data: { delayDays: 3 } },
        { id: 'n7', type: 'action', position: { x: 250, y: 970 }, data: { actionType: 'email_sent', note: `New angle: send the promised resource or a matching case study. Still no hard pitch.` } },
        { id: 'n8', type: 'delay', position: { x: 250, y: 1110 }, data: { delayDays: 3 } },
        { id: 'n9', type: 'action', position: { x: 250, y: 1230 }, data: { actionType: 'phone_call', note: `Reference your emails: "Sent you a note about X." Ask one open question.` } },
        { id: 'n10', type: 'condition', position: { x: 250, y: 1370 }, data: { condition: 'shown_interest' } },
        { id: 'n11', type: 'action', position: { x: 40, y: 1510 }, data: { actionType: 'discovery_call', note: `Book a 15-min audit call — one low-commitment rung up the ladder.` } },
        { id: 'n12', type: 'end', position: { x: 40, y: 1650 }, data: { endType: 'converted' } },
        { id: 'n13', type: 'action', position: { x: 470, y: 1510 }, data: { actionType: 'linkedin_message', note: `Share a genuinely useful resource via DM. Human and short.` } },
        { id: 'n14', type: 'delay', position: { x: 470, y: 1650 }, data: { delayDays: 4 } },
        { id: 'n15', type: 'action', position: { x: 470, y: 1770 }, data: { actionType: 'email_sent', note: `Break-up email: acknowledge timing, restate value, attach resource, leave door open.` } },
        { id: 'n16', type: 'end', position: { x: 470, y: 1910 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7'),
        createEdge('n7', 'n8'),
        createEdge('n8', 'n9'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11', 'yes'),
        createEdge('n11', 'n12'),
        createEdge('n10', 'n13', 'no'),
        createEdge('n13', 'n14'),
        createEdge('n14', 'n15'),
        createEdge('n15', 'n16'),
      ],
    }),
  },
  {
    id: 'warmup_email_phone_classic',
    name: 'Email + Phone Value Ladder',
    description: 'Two value emails, then a call — branch on interest. Reliable and simple.',
    icon: 'fa-envelope-open-text',
    color: '#3b82f6',
    category: 'warmup',
    stepCount: 6,
    totalDays: 9,
    tags: ['Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'email_sent', note: `Opener: 1 specific pain + free value (mini-audit/checklist). CTA: "Want me to send it over?"` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 3 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'email_sent', note: `Deliver the promised resource or a proof point (case study). New angle, no hard pitch.` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 3 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'phone_call', note: `Reference both emails. Ask an open question about their current process.` } },
        { id: 'n6', type: 'condition', position: { x: 250, y: 850 }, data: { condition: 'shown_interest' } },
        { id: 'n7', type: 'action', position: { x: 40, y: 990 }, data: { actionType: 'discovery_call', note: `Qualify + book an audit/demo. One rung up the value ladder.` } },
        { id: 'n8', type: 'end', position: { x: 40, y: 1130 }, data: { endType: 'converted' } },
        { id: 'n9', type: 'delay', position: { x: 470, y: 990 }, data: { delayDays: 3 } },
        { id: 'n10', type: 'action', position: { x: 470, y: 1110 }, data: { actionType: 'email_sent', note: `Break-up: short, restate value, ask permission to close the loop.` } },
        { id: 'n11', type: 'end', position: { x: 470, y: 1250 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7', 'yes'),
        createEdge('n7', 'n8'),
        createEdge('n6', 'n9', 'no'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11'),
      ],
    }),
  },
  {
    id: 'warmup_social_multichannel',
    name: 'Social Multi-Channel Touch',
    description: 'Connect on LinkedIn + Instagram, layer in email and a call. Great for founder-led brands.',
    icon: 'fa-share-alt',
    color: '#8b5cf6',
    category: 'warmup',
    stepCount: 7,
    totalDays: 10,
    tags: ['LinkedIn', 'Instagram', 'Email'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'linkedin_connect', note: `Connect with a personalized, no-pitch note.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 2 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'instagram_follow', note: `Follow + like 1-2 recent posts to build familiarity (great for D2C / founder-led).` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 2 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'email_sent', note: `Value opener with a free resource tailored to a signal you saw on their socials.` } },
        { id: 'n6', type: 'delay', position: { x: 250, y: 850 }, data: { delayDays: 3 } },
        { id: 'n7', type: 'action', position: { x: 250, y: 970 }, data: { actionType: 'linkedin_message', note: `If connected, send a short value DM (insight/resource). Otherwise comment on a post.` } },
        { id: 'n8', type: 'delay', position: { x: 250, y: 1110 }, data: { delayDays: 3 } },
        { id: 'n9', type: 'action', position: { x: 250, y: 1230 }, data: { actionType: 'phone_call', note: `Warm call referencing your email + social touches.` } },
        { id: 'n10', type: 'condition', position: { x: 250, y: 1370 }, data: { condition: 'replied' } },
        { id: 'n11', type: 'action', position: { x: 40, y: 1510 }, data: { actionType: 'sales_meeting', note: `Book the meeting.` } },
        { id: 'n12', type: 'end', position: { x: 40, y: 1650 }, data: { endType: 'converted' } },
        { id: 'n13', type: 'action', position: { x: 470, y: 1510 }, data: { actionType: 'instagram_dm', note: `Soft DM: "Sent you something useful by email — worth a look?"` } },
        { id: 'n14', type: 'end', position: { x: 470, y: 1650 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7'),
        createEdge('n7', 'n8'),
        createEdge('n8', 'n9'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11', 'yes'),
        createEdge('n11', 'n12'),
        createEdge('n10', 'n13', 'no'),
        createEdge('n13', 'n14'),
      ],
    }),
  },
  {
    id: 'warmup_website_form_blitz',
    name: 'Website Form + Email Blitz',
    description: 'Hit their website form so it gets seen, then a fast email + LinkedIn + call blitz.',
    icon: 'fa-window-maximize',
    color: '#f59e0b',
    category: 'warmup',
    stepCount: 7,
    totalDays: 10,
    tags: ['Website', 'Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'website_form', note: `Submit their contact form with a genuine, value-led message — guarantees it lands.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 1 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'email_sent', note: `Cold opener: specific pain + free lead magnet offer.` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 3 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'linkedin_connect', note: `Connection request referencing the same angle.` } },
        { id: 'n6', type: 'delay', position: { x: 250, y: 850 }, data: { delayDays: 3 } },
        { id: 'n7', type: 'action', position: { x: 250, y: 970 }, data: { actionType: 'email_sent', note: `Case study / proof point. Different angle from the first email.` } },
        { id: 'n8', type: 'delay', position: { x: 250, y: 1110 }, data: { delayDays: 3 } },
        { id: 'n9', type: 'action', position: { x: 250, y: 1230 }, data: { actionType: 'phone_call', note: `Call attempt, reference your prior touches.` } },
        { id: 'n10', type: 'condition', position: { x: 250, y: 1370 }, data: { condition: 'shown_interest' } },
        { id: 'n11', type: 'action', position: { x: 40, y: 1510 }, data: { actionType: 'discovery_call', note: `Book a low-commitment audit call.` } },
        { id: 'n12', type: 'end', position: { x: 40, y: 1650 }, data: { endType: 'converted' } },
        { id: 'n13', type: 'action', position: { x: 470, y: 1510 }, data: { actionType: 'email_sent', note: `Break-up email with one last useful resource. Leave the door open.` } },
        { id: 'n14', type: 'end', position: { x: 470, y: 1650 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7'),
        createEdge('n7', 'n8'),
        createEdge('n8', 'n9'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11', 'yes'),
        createEdge('n11', 'n12'),
        createEdge('n10', 'n13', 'no'),
        createEdge('n13', 'n14'),
      ],
    }),
  },

  // ===== RETARGETING (RE-ENGAGEMENT) TEMPLATES =====
  {
    id: 'retarget_post_call_nurture',
    name: 'Post-Call Value Nurture',
    description: 'Follow up after a call with real value: resource, case study, then re-engage.',
    icon: 'fa-hand-holding-heart',
    color: '#06b6d4',
    category: 'retargeting',
    stepCount: 6,
    totalDays: 12,
    tags: ['Email', 'Case Study', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'email_sent', note: `Recap + value: send the resource/audit you promised on the call.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 3 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'case_study_sent', note: `Send a case study matching their exact use case.` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 4 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'phone_call', note: `"Did the case study resonate? What is holding things up?"` } },
        { id: 'n6', type: 'condition', position: { x: 250, y: 850 }, data: { condition: 'shown_interest' } },
        { id: 'n7', type: 'action', position: { x: 40, y: 990 }, data: { actionType: 'sales_meeting', note: `Book the next meeting and move forward.` } },
        { id: 'n8', type: 'end', position: { x: 40, y: 1130 }, data: { endType: 'converted' } },
        { id: 'n9', type: 'delay', position: { x: 470, y: 990 }, data: { delayDays: 5 } },
        { id: 'n10', type: 'action', position: { x: 470, y: 1110 }, data: { actionType: 'email_sent', note: `Break-up: restate value, ask if timing is off, leave door open.` } },
        { id: 'n11', type: 'end', position: { x: 470, y: 1250 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7', 'yes'),
        createEdge('n7', 'n8'),
        createEdge('n6', 'n9', 'no'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11'),
      ],
    }),
  },
  {
    id: 'retarget_reengage_cold',
    name: 'Re-Engage Cold Lead',
    description: 'Reappear with a new angle across LinkedIn, email and phone. Fresh value only.',
    icon: 'fa-redo',
    color: '#ef4444',
    category: 'retargeting',
    stepCount: 6,
    totalDays: 9,
    tags: ['LinkedIn', 'Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'linkedin_profile_visit', note: `Reappear: view their profile again so your name resurfaces.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 1 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'email_sent', note: `New-angle re-engagement: reference something new (a post, industry news) + fresh value.` } },
        { id: 'n4', type: 'delay', position: { x: 250, y: 590 }, data: { delayDays: 4 } },
        { id: 'n5', type: 'action', position: { x: 250, y: 710 }, data: { actionType: 'linkedin_message', note: `Share a new resource via DM. Zero pressure.` } },
        { id: 'n6', type: 'delay', position: { x: 250, y: 850 }, data: { delayDays: 4 } },
        { id: 'n7', type: 'action', position: { x: 250, y: 970 }, data: { actionType: 'phone_call', note: `"Timing may be better now — worth a quick chat?"` } },
        { id: 'n8', type: 'condition', position: { x: 250, y: 1110 }, data: { condition: 'replied' } },
        { id: 'n9', type: 'action', position: { x: 40, y: 1250 }, data: { actionType: 'discovery_call', note: `Book a fresh discovery/audit call.` } },
        { id: 'n10', type: 'end', position: { x: 40, y: 1390 }, data: { endType: 'converted' } },
        { id: 'n11', type: 'action', position: { x: 470, y: 1250 }, data: { actionType: 'email_sent', note: `Break-up email with a final useful resource. Door stays open.` } },
        { id: 'n12', type: 'end', position: { x: 470, y: 1390 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7'),
        createEdge('n7', 'n8'),
        createEdge('n8', 'n9', 'yes'),
        createEdge('n9', 'n10'),
        createEdge('n8', 'n11', 'no'),
        createEdge('n11', 'n12'),
      ],
    }),
  },
  {
    id: 'retarget_proposal_closer',
    name: 'Proposal Follow-Up Closer',
    description: 'Chase an open proposal with ROI reminders, objection handling and gentle urgency.',
    icon: 'fa-file-signature',
    color: '#dc2626',
    category: 'retargeting',
    stepCount: 7,
    totalDays: 8,
    tags: ['Sales', 'Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'email_sent', note: `Follow up on the proposal: restate the ROI, offer to answer any questions.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 2 } },
        { id: 'n3', type: 'action', position: { x: 250, y: 450 }, data: { actionType: 'phone_call', note: `Call to surface and handle objections directly.` } },
        { id: 'n4', type: 'condition', position: { x: 250, y: 590 }, data: { condition: 'deal_closed' } },
        { id: 'n5', type: 'end', position: { x: 40, y: 730 }, data: { endType: 'converted' } },
        { id: 'n6', type: 'delay', position: { x: 470, y: 730 }, data: { delayDays: 3 } },
        { id: 'n7', type: 'action', position: { x: 470, y: 850 }, data: { actionType: 'email_sent', note: `Add gentle urgency or a small incentive. Reaffirm the value + any guarantee.` } },
        { id: 'n8', type: 'delay', position: { x: 470, y: 990 }, data: { delayDays: 3 } },
        { id: 'n9', type: 'action', position: { x: 470, y: 1110 }, data: { actionType: 'phone_call', note: `Final call attempt at a different time of day.` } },
        { id: 'n10', type: 'condition', position: { x: 470, y: 1250 }, data: { condition: 'shown_interest' } },
        { id: 'n11', type: 'end', position: { x: 320, y: 1390 }, data: { endType: 'converted' } },
        { id: 'n12', type: 'action', position: { x: 640, y: 1390 }, data: { actionType: 'email_sent', note: `Break-up: "Should I close your file for now?" Keep it graceful.` } },
        { id: 'n13', type: 'end', position: { x: 640, y: 1530 }, data: { endType: 'not_interested' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4'),
        createEdge('n4', 'n5', 'yes'),
        createEdge('n4', 'n6', 'no'),
        createEdge('n6', 'n7'),
        createEdge('n7', 'n8'),
        createEdge('n8', 'n9'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11', 'yes'),
        createEdge('n10', 'n12', 'no'),
        createEdge('n12', 'n13'),
      ],
    }),
  },
  {
    id: 'retarget_value_ladder',
    name: 'Content Value Ladder',
    description: 'Give a high-value lead magnet first, then convert engagement into a call.',
    icon: 'fa-chart-line',
    color: '#22c55e',
    category: 'retargeting',
    stepCount: 6,
    totalDays: 12,
    tags: ['Lead Magnet', 'Email', 'Call'],
    getFlowData: (triggerLabel) => ({
      nodes: [
        { id: 'trigger-1', type: 'trigger', position: { x: 250, y: 50 }, data: { triggerLabel }, draggable: false },
        { id: 'n1', type: 'action', position: { x: 250, y: 190 }, data: { actionType: 'email_sent', note: `Send a high-value lead magnet (benchmark/template) — pure give, no ask.` } },
        { id: 'n2', type: 'delay', position: { x: 250, y: 330 }, data: { delayDays: 4 } },
        { id: 'n3', type: 'condition', position: { x: 250, y: 450 }, data: { condition: 'email_clicked' } },
        { id: 'n4', type: 'action', position: { x: 40, y: 590 }, data: { actionType: 'email_sent', note: `They engaged: offer a low-commitment 10-min call to walk through it.` } },
        { id: 'n5', type: 'delay', position: { x: 40, y: 730 }, data: { delayDays: 3 } },
        { id: 'n6', type: 'action', position: { x: 40, y: 850 }, data: { actionType: 'phone_call', note: `Call to book the walkthrough.` } },
        { id: 'n7', type: 'end', position: { x: 40, y: 990 }, data: { endType: 'converted' } },
        { id: 'n8', type: 'action', position: { x: 470, y: 590 }, data: { actionType: 'linkedin_message', note: `Switch channel: share the same resource via LinkedIn DM.` } },
        { id: 'n9', type: 'delay', position: { x: 470, y: 730 }, data: { delayDays: 5 } },
        { id: 'n10', type: 'action', position: { x: 470, y: 850 }, data: { actionType: 'email_sent', note: `Break-up with one last useful resource. Leave the door open.` } },
        { id: 'n11', type: 'end', position: { x: 470, y: 990 }, data: { endType: 'nurture' } },
      ],
      edges: [
        createEdge('trigger-1', 'n1'),
        createEdge('n1', 'n2'),
        createEdge('n2', 'n3'),
        createEdge('n3', 'n4', 'yes'),
        createEdge('n4', 'n5'),
        createEdge('n5', 'n6'),
        createEdge('n6', 'n7'),
        createEdge('n3', 'n8', 'no'),
        createEdge('n8', 'n9'),
        createEdge('n9', 'n10'),
        createEdge('n10', 'n11'),
      ],
    }),
  },
];

// ========== GHOST TEMPLATE PREVIEW ==========
// Builds a greyed-out, non-interactive copy of a template's flow so it can be
// rendered directly on the canvas as a live preview before the user commits.
const buildGhostFlow = (template, triggerLabel) => {
  const { nodes, edges } = template.getFlowData(triggerLabel);
  return {
    nodes: nodes.map((n) => ({
      ...n,
      className: `ghostPreviewNode ${n.className || ''}`.trim(),
      draggable: false,
      selectable: false,
      connectable: false,
    })),
    edges: edges.map((e) => ({
      ...e,
      animated: false,
      className: 'ghostPreviewEdge',
      style: { ...(e.style || {}), stroke: '#cbd5e1', strokeWidth: 2, strokeDasharray: '6 5' },
      markerEnd: { ...(e.markerEnd || {}), color: '#cbd5e1' },
    })),
  };
};

// ========== ADD NODE PANEL ==========
const AddNodePanel = ({ onAddNode, customTouchpoints = [], flowType = 'warmup', onPreviewTemplate, previewTemplateId }) => {
  const [expanded, setExpanded] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  
  // Group touchpoints by category
  const actionCategories = [
    {
      id: 'meetings',
      label: 'Meetings & Calls',
      icon: 'fa-users',
      color: '#22c55e',
      items: [
        { actionType: 'meet_in_person', label: 'Meet In Person', icon: 'fa-users' },
        { actionType: 'online_meeting', label: 'Online Meeting', icon: 'fa-video' },
        { actionType: 'phone_call', label: 'Phone Call', icon: 'fa-phone-alt' },
      ]
    },
    {
      id: 'sales',
      label: 'Sales Process',
      icon: 'fa-briefcase',
      color: '#f59e0b',
      items: [
        { actionType: 'demo_given', label: 'Demo Given', icon: 'fa-play-circle' },
        { actionType: 'proposal_sent', label: 'Proposal Sent', icon: 'fa-file-alt' },
        { actionType: 'quote_sent', label: 'Quote Sent', icon: 'fa-file-invoice-dollar' },
        { actionType: 'contract_sent', label: 'Contract Sent', icon: 'fa-file-contract' },
        { actionType: 'contract_signed', label: 'Contract Signed', icon: 'fa-file-signature' },
        { actionType: 'invoice_sent', label: 'Invoice Sent', icon: 'fa-file-invoice' },
        { actionType: 'payment_received', label: 'Payment Received', icon: 'fa-money-bill-wave' },
        { actionType: 'onboarding', label: 'Onboarding', icon: 'fa-rocket' },
      ]
    },
    {
      id: 'email',
      label: 'Email',
      icon: 'fa-envelope',
      color: '#3b82f6',
      items: [
        { actionType: 'email_sent', label: 'Send Email', icon: 'fa-paper-plane' },
        { actionType: 'newsletter', label: 'Send Newsletter', icon: 'fa-newspaper' },
      ]
    },
    {
      id: 'messaging',
      label: 'Messaging',
      icon: 'fa-comments',
      color: '#06b6d4',
      items: [
        { actionType: 'whatsapp_message', label: 'WhatsApp', icon: 'fa-whatsapp', isBrand: true },
        { actionType: 'sms_sent', label: 'SMS', icon: 'fa-sms' },
        { actionType: 'voice_message', label: 'Voice Message', icon: 'fa-microphone' },
        { actionType: 'loom_video', label: 'Loom Video', icon: 'fa-video' },
        { actionType: 'video_message', label: 'Video Message', icon: 'fa-film' },
        { actionType: 'slack_message', label: 'Slack Message', icon: 'fa-slack', isBrand: true },
        { actionType: 'website_form', label: 'Website Form', icon: 'fa-wpforms', isBrand: true },
      ]
    },
    {
      id: 'social',
      label: 'Social Media',
      icon: 'fa-share-alt',
      color: '#8b5cf6',
      items: [
        { actionType: 'linkedin_profile_visit', label: 'LinkedIn Profile Visit', icon: 'fa-linkedin', isBrand: true },
        { actionType: 'linkedin_connect', label: 'LinkedIn Connect', icon: 'fa-linkedin', isBrand: true },
        { actionType: 'linkedin_message', label: 'LinkedIn DM', icon: 'fa-linkedin', isBrand: true },
        { actionType: 'linkedin_comment', label: 'LinkedIn Comment', icon: 'fa-comment' },
        { actionType: 'linkedin_like', label: 'LinkedIn Like', icon: 'fa-thumbs-up' },
        { actionType: 'instagram_follow', label: 'Instagram Follow', icon: 'fa-instagram', isBrand: true },
        { actionType: 'instagram_interaction', label: 'Instagram Interaction', icon: 'fa-instagram', isBrand: true },
        { actionType: 'instagram_dm', label: 'Instagram DM', icon: 'fa-instagram', isBrand: true },
        { actionType: 'instagram_comment', label: 'Instagram Comment', icon: 'fa-comment' },
        { actionType: 'twitter_follow', label: 'Twitter/X Follow', icon: 'fa-twitter', isBrand: true },
        { actionType: 'twitter_dm', label: 'Twitter/X DM', icon: 'fa-twitter', isBrand: true },
        { actionType: 'facebook_message', label: 'Facebook Message', icon: 'fa-facebook', isBrand: true },
      ]
    },
    {
      id: 'content',
      label: 'Content & Resources',
      icon: 'fa-gift',
      color: '#ec4899',
      items: [
        { actionType: 'lead_magnet', label: 'Lead Magnet', icon: 'fa-magnet' },
        { actionType: 'ebook_sent', label: 'eBook Sent', icon: 'fa-book' },
        { actionType: 'whitepaper_sent', label: 'Whitepaper Sent', icon: 'fa-file-pdf' },
        { actionType: 'case_study_sent', label: 'Case Study Sent', icon: 'fa-chart-line' },
        { actionType: 'webinar', label: 'Webinar Invite', icon: 'fa-desktop' },
        { actionType: 'workshop_invite', label: 'Workshop Invite', icon: 'fa-chalkboard-teacher' },
        { actionType: 'free_trial', label: 'Free Trial Offered', icon: 'fa-gift' },
        { actionType: 'discount_offer', label: 'Discount Offer', icon: 'fa-percent' },
      ]
    },
    {
      id: 'tasks',
      label: 'Tasks & Internal',
      icon: 'fa-tasks',
      color: '#64748b',
      items: [
        { actionType: 'add_tag', label: 'Add Tag', icon: 'fa-tag' },
        // Structural node (not an action touchpoint): pauses the flow and shows
        // the Notes composer to the user. `nodeType` makes the drag handler
        // create a `log_notes` node instead of an action node.
        { nodeType: 'log_notes', label: 'Log Notes', icon: 'fa-clipboard-list' },
      ]
    },
  ];

  // Add custom touchpoints if any
  if (customTouchpoints.length > 0) {
    actionCategories.push({
      id: 'custom',
      label: 'Custom',
      icon: 'fa-puzzle-piece',
      color: '#f97316',
      items: customTouchpoints.map(tp => ({
        actionType: `custom_${tp.id}`,
        label: tp.label,
        icon: tp.icon || 'fa-circle',
      }))
    });
  }

  const flowControlItems = [
    { type: 'delay', label: 'Wait / Delay', icon: 'fa-hourglass-half', color: '#f59e0b' },
    { type: 'split', label: 'A/B Split', icon: 'fa-code-branch', color: '#8b5cf6' },
    { type: 'condition', label: 'Condition', icon: 'fa-question', color: '#ec4899' },
    { type: 'data_capture', label: 'Capture Data', icon: 'fa-database', color: '#7c3aed' },
    { type: 'tag', label: 'Add Tags', icon: 'fa-tags', color: '#14b8a6' },
    { type: 'end', label: 'End Flow', icon: 'fa-flag-checkered', color: '#6b7280' },
  ];

  const automationItems = [
    { type: 'auto_send_email', label: 'Auto Send Email', icon: 'fa-paper-plane', color: '#0ea5e9' },
    { type: 'auto_check_reply', label: 'Check for Reply', icon: 'fa-envelope-open-text', color: '#8b5cf6' },
  ];

  const filteredTemplates = FLOW_TEMPLATES.filter(t => t.category === flowType);

  const handleTemplateClick = (template) => {
    if (onPreviewTemplate) onPreviewTemplate(template);
  };

  return (
    <div className="addNodePanel">
      <div className="addNodePanelHeader">
        <i className="fa fa-plus-circle"></i>
        <span>Add Node</span>
      </div>
      <div className="addNodeCategories">
        {/* Flow Templates Section */}
        <div className="addNodeCategory templateCategory">
          <button
            className={`addNodeCategoryHeader templateHeader ${templatesOpen ? 'expanded' : ''}`}
            onClick={() => { setTemplatesOpen(!templatesOpen); if (!templatesOpen) setExpanded(null); }}
            style={{ '--cat-color': '#f59e0b' }}
          >
            <i className="fa fa-magic"></i>
            <span>Flow Templates</span>
            <span className="templateBadge">{filteredTemplates.length}</span>
            <i className="fa fa-chevron-down chevron"></i>
          </button>
          {templatesOpen && (
            <div className="templateList">
              {filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  className={`templateCard ${previewTemplateId === template.id ? 'active' : ''}`}
                  onClick={() => handleTemplateClick(template)}
                  onMouseEnter={() => onPreviewTemplate && onPreviewTemplate(template)}
                  style={{ '--tpl-color': template.color }}
                >
                  <div className="templateCardIcon">
                    <i className={`fa ${template.icon}`}></i>
                  </div>
                  <div className="templateCardContent">
                    <span className="templateCardName">{template.name}</span>
                    <span className="templateCardDesc">{template.description}</span>
                    <div className="templateCardMeta">
                      <span><i className="fa fa-layer-group"></i> {template.stepCount} steps</span>
                      <span><i className="fa fa-clock"></i> {template.totalDays}d</span>
                    </div>
                    <div className="templateCardTags">
                      {template.tags.map((tag, i) => (
                        <span key={i} className="templateTag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="templateCardArrow">
                    <i className="fa fa-eye"></i>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="addNodeDivider"></div>

        {/* Action Categories */}
        {actionCategories.map((cat) => (
          <div key={cat.id} className="addNodeCategory">
            <button
              className={`addNodeCategoryHeader ${expanded === cat.id ? 'expanded' : ''}`}
              onClick={() => setExpanded(expanded === cat.id ? null : cat.id)}
              style={{ '--cat-color': cat.color }}
            >
              <i className={`fa ${cat.icon}`}></i>
              <span>{cat.label}</span>
              <i className="fa fa-chevron-down chevron"></i>
            </button>
            {expanded === cat.id && (
              <div className="addNodeCategoryItems">
                {cat.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="addNodeOption"
                    style={{ '--node-color': cat.color }}
                    draggable
                    onDragStart={(e) => {
                      const payload = item.nodeType
                        ? { type: item.nodeType }
                        : { type: 'action', actionType: item.actionType };
                      e.dataTransfer.setData('application/reactflow', JSON.stringify(payload));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  >
                    <i className="fa fa-grip-vertical dragIndicator"></i>
                    <i className={`${item.isBrand ? 'fab' : 'fa'} ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Divider */}
        <div className="addNodeDivider"></div>

        {/* Flow Control */}
        <div className="addNodeCategory">
          <button
            className={`addNodeCategoryHeader ${expanded === 'flow' ? 'expanded' : ''}`}
            onClick={() => setExpanded(expanded === 'flow' ? null : 'flow')}
            style={{ '--cat-color': '#8b5cf6' }}
          >
            <i className="fa fa-sitemap"></i>
            <span>Flow Control</span>
            <i className="fa fa-chevron-down chevron"></i>
          </button>
          {expanded === 'flow' && (
            <div className="addNodeCategoryItems">
              {flowControlItems.map((item, idx) => (
                <div
                  key={idx}
                  className="addNodeOption"
                  style={{ '--node-color': item.color }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/reactflow', JSON.stringify({ type: item.type }));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  <i className="fa fa-grip-vertical dragIndicator"></i>
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="addNodeDivider"></div>

        {/* Automation */}
        <div className="addNodeCategory">
          <button
            className={`addNodeCategoryHeader ${expanded === 'automation' ? 'expanded' : ''}`}
            onClick={() => setExpanded(expanded === 'automation' ? null : 'automation')}
            style={{ '--cat-color': '#0ea5e9' }}
          >
            <i className="fa fa-robot"></i>
            <span>Automation</span>
            <i className="fa fa-chevron-down chevron"></i>
          </button>
          {expanded === 'automation' && (
            <div className="addNodeCategoryItems">
              {automationItems.map((item, idx) => (
                <div
                  key={idx}
                  className="addNodeOption"
                  style={{ '--node-color': item.color }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/reactflow', JSON.stringify({ type: item.type }));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  <i className="fa fa-grip-vertical dragIndicator"></i>
                  <i className={`fa ${item.icon}`}></i>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== EDIT NODE PANEL (Sidebar) ==========
const EditNodePanel = ({ node, onClose, onSave, customTouchpoints = [], emailTopics = [], senderEmails = [], followsLogNotes = false }) => {
  const [nodeData, setNodeData] = useState(node?.data || {});

  // Update nodeData when node changes
  React.useEffect(() => {
    setNodeData(node?.data || {});
  }, [node?.id]);

  if (!node) return null;

  const handleSave = () => {
    if (node.type === 'auto_send_email' && !nodeData.templateId) {
      alert('Please select an email template before saving.');
      return;
    }
    // Backend preparation needs a concrete template to generate from — it can't
    // resolve the "current step default" without a logged-in session.
    if (node.type === 'action' && nodeData.actionType === 'email_sent' && nodeData.prepareInBackend && !nodeData.templateId) {
      alert('Please select a specific email template to pre-prepare this email in the backend.');
      return;
    }
    onSave(node.id, nodeData);
    onClose();
  };

  // Get node type config for header styling
  const getNodeConfig = () => {
    switch (node.type) {
      case 'action': return { icon: 'fa-bolt', color: '#3b82f6', label: 'Action' };
      case 'delay': return { icon: 'fa-hourglass-half', color: '#f59e0b', label: 'Delay' };
      case 'split': return { icon: 'fa-code-branch', color: '#8b5cf6', label: 'A/B Split' };
      case 'condition': return { icon: 'fa-question', color: '#ec4899', label: 'Condition' };
      case 'tag': return { icon: 'fa-tags', color: '#14b8a6', label: 'Add Tags' };
      case 'end': return { icon: 'fa-flag-checkered', color: '#6b7280', label: 'End Flow' };
      case 'auto_send_email': return { icon: 'fa-paper-plane', color: '#0ea5e9', label: 'Auto Send Email' };
      case 'auto_check_reply': return { icon: 'fa-envelope-open-text', color: '#8b5cf6', label: 'Check for Reply' };
      case 'data_capture': return { icon: 'fa-clipboard-list', color: '#7c3aed', label: 'Capture Data' };
      case 'log_notes': return { icon: 'fa-clipboard-list', color: '#0ea5e9', label: 'Log Notes' };
      default: return { icon: 'fa-circle', color: '#9ca3af', label: 'Node' };
    }
  };

  const nodeConfig = getNodeConfig();

  // Merge custom touchpoints with predefined ones
  const allTouchpoints = {
    ...TOUCHPOINT_OPTIONS,
    ...customTouchpoints.reduce((acc, tp) => {
      acc[`custom_${tp.id}`] = { label: tp.label, icon: tp.icon, points: tp.points, category: 'Custom' };
      return acc;
    }, {})
  };

  // Group touchpoints by category. Legacy email types are hidden from the
  // dropdown, except the one currently selected on this node so an old flow
  // still shows its correct value.
  const currentActionType = nodeData.actionType;
  const groupedTouchpoints = Object.entries(allTouchpoints).reduce((acc, [key, tp]) => {
    if (tp.legacy && key !== currentActionType) return acc;
    const cat = tp.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ key, ...tp });
    return acc;
  }, {});

  // "Send Email" and "Send Newsletter" are manual email steps that can carry a
  // template + sender override consumed by Normal/Bulk mode in the Targets list.
  const isEmailActionNode = node.type === 'action' && currentActionType === 'email_sent';

  return (
    <div className="editNodePanel">
      <div className="editNodePanelHeader" style={{ '--node-color': nodeConfig.color }}>
        <div className="editNodePanelTitle">
          <div className="editNodePanelIcon">
            <i className={`fa ${nodeConfig.icon}`}></i>
          </div>
          <span>{nodeConfig.label}</span>
        </div>
        <button className="editNodePanelClose" onClick={onClose}>
          <i className="fa fa-times"></i>
        </button>
      </div>
      
      <div className="editNodePanelBody">
          {node.type === 'action' && (
            <>
              <div className="editNodeField">
                <label>Action Type</label>
                <select
                  value={nodeData.actionType || 'email_sent'}
                  onChange={(e) => setNodeData({ ...nodeData, actionType: e.target.value })}
                >
                  {Object.entries(groupedTouchpoints).map(([category, items]) => (
                    <optgroup key={category} label={category}>
                      {items.map((tp) => (
                        <option key={tp.key} value={tp.key}>{tp.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="editNodeField">
                <label>Note (optional)</label>
                <input
                  type="text"
                  placeholder="Add a note..."
                  value={nodeData.note || ''}
                  onChange={(e) => setNodeData({ ...nodeData, note: e.target.value })}
                />
              </div>

              {isEmailActionNode && (
                <>
                  <div className="editNodeField">
                    <label>Email Template</label>
                    <select
                      value={nodeData.templateId || ''}
                      onChange={(e) => {
                        const selected = emailTopics.find(t => t.id === e.target.value);
                        setNodeData({
                          ...nodeData,
                          templateId: e.target.value,
                          templateName: selected?.topic || selected?.name || '',
                        });
                      }}
                    >
                      <option value="">Use current step default</option>
                      {emailTopics.map((topic) => (
                        <option key={topic.id} value={topic.id}>
                          {topic.topic || topic.name}
                        </option>
                      ))}
                    </select>
                    <span className="editNodeHelp">
                      Leave on "default" to send whichever template matches the contact's current step (Intro / Follow-up).
                    </span>
                  </div>

                  <div className="editNodeField">
                    <label>Sender</label>
                    <select
                      value={nodeData.senderMode || 'default'}
                      onChange={(e) => setNodeData({ ...nodeData, senderMode: e.target.value })}
                    >
                      <option value="default">Default (last used sender)</option>
                      <option value="fixed">Fixed account</option>
                      <option value="random">Random (rotate through all)</option>
                      <option value="choose">Choose accounts (even split)</option>
                    </select>
                  </div>

                  {nodeData.senderMode === 'fixed' && (
                    <div className="editNodeField">
                      <label>Sender Account</label>
                      <select
                        value={nodeData.senderId || ''}
                        onChange={(e) => {
                          const sender = senderEmails.find(s => s.id === e.target.value);
                          setNodeData({
                            ...nodeData,
                            senderId: e.target.value,
                            senderName: sender?.sender_name || sender?.email || '',
                          });
                        }}
                      >
                        <option value="">-- Select an account --</option>
                        {senderEmails.map((sender) => (
                          <option key={sender.id} value={sender.id}>
                            {sender.sender_name ? `${sender.sender_name} (${sender.email})` : sender.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {nodeData.senderMode === 'choose' && (
                    <div className="editNodeField">
                      <label>Accounts to rotate</label>
                      <div className="editNodeCheckboxList">
                        {senderEmails.length === 0 && (
                          <span className="editNodeHelp">No sender accounts configured yet.</span>
                        )}
                        {senderEmails.map((sender) => {
                          const pool = Array.isArray(nodeData.senderPool) ? nodeData.senderPool : [];
                          const checked = pool.includes(sender.id);
                          return (
                            <label key={sender.id} className={`editNodeCheckboxItem ${checked ? 'checked' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...pool, sender.id]
                                    : pool.filter(id => id !== sender.id);
                                  setNodeData({ ...nodeData, senderPool: next });
                                }}
                              />
                              <span className="editNodeCheckboxMark">
                                <i className="fa fa-check"></i>
                              </span>
                              <span className="editNodeCheckboxText">
                                <span className="editNodeCheckboxName">{sender.sender_name || sender.email}</span>
                                {sender.sender_name && (
                                  <span className="editNodeCheckboxEmail">{sender.email}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="editNodeField editNodePrepareRow">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={nodeData.prepareInBackend ? 'true' : 'false'}
                      className={`editNodePrepareSwitch ${nodeData.prepareInBackend ? 'on' : ''}`}
                      onClick={() => setNodeData({ ...nodeData, prepareInBackend: !nodeData.prepareInBackend })}
                    >
                      <span className="editNodePrepareSwitchInfo">
                        <span className="editNodePrepareSwitchTitle">
                          <i className="fa fa-wand-magic-sparkles"></i>
                          Pre-prepare email in backend
                        </span>
                        <span className="editNodePrepareSwitchState">{nodeData.prepareInBackend ? 'On' : 'Off'}</span>
                      </span>
                      <span className="editNodePrepareSwitchTrack">
                        <span className="editNodePrepareSwitchThumb"></span>
                      </span>
                    </button>
                    <span className="editNodeHelp">
                      {nodeData.prepareInBackend
                        ? 'The flow generates this email in advance (uses credits at prep time). Send it instantly from the World DB "Prepared" tab — no AI wait.'
                        : 'Turn on to pre-generate this email in the background so you can send it instantly later.'}
                    </span>
                  </div>

                  <div className="editNodeHint" style={{ borderLeftColor: '#3b82f6' }}>
                    <i className="fa fa-paper-plane" style={{ color: '#3b82f6' }}></i>
                    <span>When a contact is on this step, sending from the Targets list (Normal or Bulk mode) uses this template and sender.</span>
                  </div>
                </>
              )}
            </>
          )}
          
          {node.type === 'delay' && (
            <div className="editNodeField">
              <label>Wait Duration (days)</label>
              <input
                type="number"
                min="1"
                value={nodeData.delayDays || 1}
                onChange={(e) => setNodeData({ ...nodeData, delayDays: parseInt(e.target.value) || 1 })}
              />
            </div>
          )}
          
          {node.type === 'split' && (
            <div className="editNodeField">
              <label>Split Ratio</label>
              <div className="splitRatioInput">
                <div className="splitInput">
                  <span>A</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={nodeData.splitA || 50}
                    onChange={(e) => {
                      const valA = parseInt(e.target.value) || 0;
                      setNodeData({ ...nodeData, splitA: valA, splitB: 100 - valA });
                    }}
                  />
                  <span>%</span>
                </div>
                <div className="splitInput">
                  <span>B</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={nodeData.splitB || 50}
                    onChange={(e) => {
                      const valB = parseInt(e.target.value) || 0;
                      setNodeData({ ...nodeData, splitB: valB, splitA: 100 - valB });
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
          )}
          
          {node.type === 'condition' && (
            <>
              <div className="editNodeField">
                <label>Condition Type</label>
                <select
                  value={nodeData.condition || 'email_opened'}
                  onChange={(e) => setNodeData({ ...nodeData, condition: e.target.value, customQuestion: '' })}
                >
                  <optgroup label="Automatic (System checks)">
                    <option value="is_favorite">⭐ Is Favorite</option>
                    <option value="email_opened">Email Opened</option>
                    <option value="email_clicked">Email Clicked</option>
                    <option value="replied">Replied to Message</option>
                    <option value="score_above">Score Above Threshold</option>
                    <option value="has_phone">Has Phone Number</option>
                    <option value="has_email">Has Email</option>
                    <option value="has_company">Has Company</option>
                  </optgroup>
                  <optgroup label="Manual Decision (You decide)">
                    <option value="shown_interest">Has Shown Interest?</option>
                    <option value="qualified">Is Qualified?</option>
                    <option value="decision_maker">Is Decision Maker?</option>
                    <option value="has_budget">Has Budget?</option>
                    <option value="good_fit">Good Fit?</option>
                    <option value="ready_to_buy">Ready to Buy?</option>
                    <option value="needs_follow_up">Needs Follow Up?</option>
                    <option value="meeting_went_well">Meeting Went Well?</option>
                    <option value="custom_question">Custom Question...</option>
                  </optgroup>
                  <optgroup label="Analytics Tracking (logs to Sales Analytics)">
                    <option value="responded_positively">📊 Responded Positively?</option>
                    <option value="meeting_booked">📊 Meeting Booked?</option>
                    <option value="deal_closed">📊 Deal Closed?</option>
                  </optgroup>
                </select>
              </div>
              
              {nodeData.condition === 'custom_question' && (
                <div className="editNodeField">
                  <label>Your Question</label>
                  <input
                    type="text"
                    placeholder="e.g., Did they mention competitors?"
                    value={nodeData.customQuestion || ''}
                    onChange={(e) => setNodeData({ ...nodeData, customQuestion: e.target.value })}
                  />
                </div>
              )}
              
              {CONDITION_OPTIONS[nodeData.condition]?.type === 'manual' && (
                <div className="editNodeHint">
                  <i className="fa fa-info-circle"></i>
                  <span>This will show as a Yes/No question in the lead view. You'll manually choose the path.</span>
                </div>
              )}
              {CONDITION_OPTIONS[nodeData.condition]?.analytics && (
                <div className="editNodeHint" style={{ borderLeftColor: '#7c3aed' }}>
                  <i className="fa fa-chart-bar" style={{ color: '#7c3aed' }}></i>
                  <span>Analytics: Your answer is automatically recorded in Sales Analytics (anonymized).</span>
                </div>
              )}
            </>
          )}
          
          {node.type === 'tag' && (
            <>
              <div className="editNodeField">
                <label>Select Tags to Add</label>
                <div className="editTagsGrid">
                  {PREDEFINED_TAGS.map((tag) => {
                    const isSelected = (nodeData.tags || []).some(t => t.id === tag.id);
                    return (
                      <button
                        key={tag.id}
                        className={`editTagOption ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          const currentTags = nodeData.tags || [];
                          if (isSelected) {
                            setNodeData({ ...nodeData, tags: currentTags.filter(t => t.id !== tag.id) });
                          } else {
                            setNodeData({ ...nodeData, tags: [...currentTags, tag] });
                          }
                        }}
                        style={{ 
                          '--tag-color': tag.color,
                          borderColor: isSelected ? tag.color : '#e5e7eb',
                          backgroundColor: isSelected ? `${tag.color}15` : '#ffffff'
                        }}
                      >
                        <i className={`fa ${tag.icon}`} style={{ color: tag.color }}></i>
                        <span>{tag.name}</span>
                        {isSelected && <i className="fa fa-check checkIcon"></i>}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="editNodeField">
                <label>Or Add Custom Tag</label>
                <div className="customTagInput">
                  <input
                    type="text"
                    placeholder="Tag name..."
                    id="customTagName"
                  />
                  <input
                    type="color"
                    defaultValue="#6b7280"
                    id="customTagColor"
                  />
                  <button
                    className="addCustomTagBtn"
                    onClick={() => {
                      const nameInput = document.getElementById('customTagName');
                      const colorInput = document.getElementById('customTagColor');
                      if (nameInput.value.trim()) {
                        const customTag = {
                          id: `custom_${Date.now()}`,
                          name: nameInput.value.trim(),
                          color: colorInput.value,
                          icon: 'fa-tag'
                        };
                        setNodeData({ 
                          ...nodeData, 
                          tags: [...(nodeData.tags || []), customTag] 
                        });
                        nameInput.value = '';
                      }
                    }}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>
              
              {(nodeData.tags || []).length > 0 && (
                <div className="editNodeField">
                  <label>Selected Tags ({(nodeData.tags || []).length})</label>
                  <div className="selectedTagsList">
                    {(nodeData.tags || []).map((tag, idx) => (
                      <span 
                        key={idx} 
                        className="selectedTagPill"
                        style={{ backgroundColor: tag.color }}
                      >
                        <i className={`fa ${tag.icon || 'fa-tag'}`}></i>
                        {tag.name}
                        <button 
                          onClick={() => {
                            setNodeData({ 
                              ...nodeData, 
                              tags: (nodeData.tags || []).filter((_, i) => i !== idx) 
                            });
                          }}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="editNodeHint">
                <i className="fa fa-info-circle"></i>
                <span>These tags will be automatically added to the lead when this step is reached.</span>
              </div>
            </>
          )}
          
          {node.type === 'data_capture' && (
            <>
              <div className="editNodeField">
                <label>Fields to Capture</label>
                <div className="editTagsGrid">
                  {[
                    { id: 'industry', name: 'Industry', icon: 'fa-industry', color: '#7c3aed' },
                    { id: 'size', name: 'Company Size', icon: 'fa-users', color: '#3b82f6' },
                    { id: 'deal_value', name: 'Deal Value', icon: 'fa-euro-sign', color: '#22c55e' },
                  ].map((field) => {
                    const isSelected = (nodeData.captureFields || []).includes(field.id);
                    return (
                      <button
                        key={field.id}
                        className={`editTagOption ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          const current = nodeData.captureFields || [];
                          setNodeData({
                            ...nodeData,
                            captureFields: isSelected
                              ? current.filter(f => f !== field.id)
                              : [...current, field.id],
                          });
                        }}
                        style={{
                          '--tag-color': field.color,
                          borderColor: isSelected ? field.color : '#e5e7eb',
                          backgroundColor: isSelected ? `${field.color}15` : '#ffffff',
                        }}
                      >
                        <i className={`fa ${field.icon}`} style={{ color: field.color }}></i>
                        <span>{field.name}</span>
                        {isSelected && <i className="fa fa-check checkIcon"></i>}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="editNodeHint">
                <i className="fa fa-info-circle"></i>
                <span>Shows a form in SalesGod Swipe. User fills in the data, then the flow advances. Data is saved to CRM + Analytics.</span>
              </div>
            </>
          )}

          {node.type === 'log_notes' && (
            <>
              <div className="editNodeField">
                <label>Instruction (optional)</label>
                <textarea
                  value={nodeData.note || ''}
                  onChange={(e) => setNodeData({ ...nodeData, note: e.target.value })}
                  placeholder="e.g. Log what was said on the call — pain points, objections, next steps…"
                  rows={3}
                  style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div className="editNodeHint">
                <i className="fa fa-info-circle"></i>
                <span>Pauses the flow and shows the Notes composer. The user picks the activity (call, meeting…), outcome, sentiment and writes a note to complete the step. Saved to the contact's notes.</span>
              </div>
            </>
          )}

          {node.type === 'end' && (
            <>
              <div className="editNodeField">
                <label>End Type</label>
                <select
                  value={nodeData.endType || 'completed'}
                  onChange={(e) => setNodeData({ ...nodeData, endType: e.target.value })}
                  className="endTypeSelect"
                >
                  <option value="completed">✅ Completed - Flow finished successfully</option>
                  <option value="converted">🎉 Converted - Became a customer</option>
                  <option value="disqualified">❌ Disqualified - Not a fit</option>
                  <option value="not_interested">🚫 Not Interested - No response/interest</option>
                  <option value="wrong_contact">⚠️ Wrong Contact - Invalid data</option>
                  <option value="nurture">💤 Nurture - Keep for later</option>
                </select>
              </div>
              
              <div className={`endTypePreview ${nodeData.endType || 'completed'}`}>
                {nodeData.endType === 'disqualified' || nodeData.endType === 'not_interested' || nodeData.endType === 'wrong_contact' ? (
                  <div className="endTypeWarning">
                    <i className="fa fa-exclamation-triangle"></i>
                    <span>Contact will be marked as inactive. Actions will be blocked.</span>
                  </div>
                ) : nodeData.endType === 'converted' ? (
                  <div className="endTypeSuccess">
                    <i className="fa fa-trophy"></i>
                    <span>Contact will be marked as converted!</span>
                  </div>
                ) : (
                  <div className="endTypeInfo">
                    <i className="fa fa-info-circle"></i>
                    <span>Flow will end and contact stays active.</span>
                  </div>
                )}
              </div>
            </>
          )}
          
          {node.type === 'auto_send_email' && (
            <>
              <div className="editNodeField">
                <label>Email Template / Topic</label>
                <select
                  value={nodeData.templateId || ''}
                  onChange={(e) => {
                    const selected = emailTopics.find(t => t.id === e.target.value);
                    setNodeData({ 
                      ...nodeData, 
                      templateId: e.target.value, 
                      templateName: selected?.topic || selected?.name || '' 
                    });
                  }}
                >
                  <option value="">-- Select a template --</option>
                  {emailTopics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.topic || topic.name}
                    </option>
                  ))}
                </select>
              </div>

              {followsLogNotes && nodeData.templateId && (() => {
                const usesCalls = emailTopics.find(t => t.id === nodeData.templateId)?.callContext?.enabled === true;
                const color = usesCalls ? '#10b981' : '#f59e0b';
                return (
                  <div className="editNodeHint" style={{ borderLeftColor: color }}>
                    <i className={`fa ${usesCalls ? 'fa-phone-volume' : 'fa-triangle-exclamation'}`} style={{ color }}></i>
                    <span>
                      {usesCalls
                        ? 'This email follows "Log Notes" and uses the call notes logged there as context.'
                        : 'This email follows "Log Notes", but the selected template ignores call notes. Turn on "Call Context" in the template so the email builds on what was said.'}
                    </span>
                  </div>
                );
              })()}

              <div className="editNodeField">
                <label>Sender Account</label>
                <select
                  value={nodeData.senderId || 'random'}
                  onChange={(e) => {
                    const val = e.target.value;
                    const sender = senderEmails.find(s => s.id === val);
                    setNodeData({
                      ...nodeData,
                      senderId: val,
                      senderName: sender?.sender_name || sender?.email || '',
                    });
                  }}
                >
                  <option value="random">Random (rotate through all)</option>
                  {senderEmails.map((sender) => (
                    <option key={sender.id} value={sender.id}>
                      {sender.sender_name ? `${sender.sender_name} (${sender.email})` : sender.email}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="editNodeHint" style={{ borderLeftColor: '#0ea5e9' }}>
                <i className="fa fa-robot" style={{ color: '#0ea5e9' }}></i>
                <span>This email will be sent automatically by the system when the contact reaches this step. No manual action required.</span>
              </div>
            </>
          )}
          
          {node.type === 'auto_check_reply' && (
            <>
              <div className="editNodeField">
                <label>Timeout (Days without reply)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={nodeData.timeoutDays || 3}
                  onChange={(e) => setNodeData({ ...nodeData, timeoutDays: parseInt(e.target.value) || 3 })}
                />
              </div>
              
              <div className="editNodeHint" style={{ borderLeftColor: '#8b5cf6' }}>
                <i className="fa fa-robot" style={{ color: '#8b5cf6' }}></i>
                <span>The system checks the inbox automatically. If a reply is found, the "Reply" path is taken. After the timeout, the "No Reply" path is taken.</span>
              </div>
            </>
          )}
        </div>
      
      <div className="editNodePanelActions">
        <button className="editNodePanelCancel" onClick={onClose}>Cancel</button>
        <button className="editNodePanelSave" onClick={handleSave}>
          <i className="fa fa-check"></i> Apply
        </button>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT (Inner) ==========
const FlowBuilderCanvasInner = ({ 
  initialNodes = [], 
  initialEdges = [], 
  onFlowChange,
  flowName = '',
  onFlowNameChange,
  customTouchpoints = [],
  triggerLabel = 'When Lead Created',
  flowType = 'warmup',
  emailTopics = [],
  senderEmails = [],
}) => {
  const reactFlowWrapper = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();
  
  // Default initial nodes if none provided
  const defaultNodes = [
    {
      id: 'trigger-1',
      type: 'trigger',
      position: { x: 250, y: 50 },
      data: { triggerLabel },
      draggable: false,
    },
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes.length > 0 ? initialNodes : defaultNodes
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [editingNode, setEditingNode] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Update trigger label when it changes
  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.type === 'trigger' ? { ...node, data: { ...node.data, triggerLabel } } : node
      )
    );
  }, [triggerLabel, setNodes]);

  // Add node handlers to node data
  const nodesWithHandlers = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        onEdit: (id) => {
          const nodeToEdit = nodes.find((n) => n.id === id);
          setEditingNode(nodeToEdit);
        },
        onDelete: (id) => {
          setNodes((nds) => nds.filter((n) => n.id !== id));
          setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
        },
      },
    }));
  }, [nodes, setNodes, setEdges]);

  // Greyed-out, non-interactive template preview shown directly on the canvas.
  const ghostFlow = useMemo(
    () => (previewTemplate ? buildGhostFlow(previewTemplate, triggerLabel) : null),
    [previewTemplate, triggerLabel]
  );
  const isPreviewing = !!ghostFlow;
  const displayNodes = isPreviewing ? ghostFlow.nodes : nodesWithHandlers;
  const displayEdges = isPreviewing ? ghostFlow.edges : edges;

  // Re-frame the canvas whenever we switch in/out of preview mode.
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => {
      try { fitView({ padding: 0.2, duration: 300 }); } catch (e) { /* noop */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [previewTemplate, fitView]);

  // Handle edge connections
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        type: 'smoothstep',
        animated: true,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#3b82f6',
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  // Handle edge click - delete edge on click
  const onEdgeClick = useCallback(
    (event, edge) => {
      event.stopPropagation();
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges]
  );

  // Add new node - no auto-connect, user draws connections manually
  const handleAddNode = (type, actionType, position = null) => {
    const newId = `${type}-${Date.now()}`;
    
    // Use provided position or calculate a default one
    let newPosition;
    if (position) {
      newPosition = position;
    } else {
      const existingNodes = nodes.length;
      const baseX = 250;
      const baseY = 200;
      newPosition = {
        x: baseX + (existingNodes % 3) * 50,
        y: baseY + Math.floor(existingNodes / 1) * 130,
      };
    }

    const newNodeData = {
      action: { actionType: actionType || 'email_sent', note: '' },
      delay: { delayDays: 1 },
      split: { splitA: 50, splitB: 50 },
      condition: { condition: 'email_opened' },
      data_capture: { captureFields: ['industry', 'size'] },
      log_notes: { note: '' },
      tag: { tags: [] },
      end: { endType: 'completed' },
      auto_send_email: { templateId: '', templateName: '', senderId: 'random', senderName: '' },
      auto_check_reply: { timeoutDays: 3 },
    };

    const newNode = {
      id: newId,
      type,
      position: newPosition,
      data: newNodeData[type] || {},
    };

    setNodes((nds) => [...nds, newNode]);
  };

  // Handle drag over for drop zone
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop - create node at drop position
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      const { type, actionType } = JSON.parse(data);
      
      // Get the position where the node was dropped
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      handleAddNode(type, actionType, position);
    },
    [screenToFlowPosition]
  );

  // Load a flow template
  const handleLoadTemplate = useCallback((template) => {
    const hasExistingNodes = nodes.filter(n => n.type !== 'trigger').length > 0;
    if (hasExistingNodes) {
      if (!window.confirm('This will replace your current flow. Continue?')) return;
    }
    const flowData = template.getFlowData(triggerLabel);
    setNodes(flowData.nodes);
    setEdges(flowData.edges);
  }, [nodes, triggerLabel, setNodes, setEdges]);

  // Save node edits
  const handleSaveNode = (nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  // Notify parent of changes
  const handleSaveFlow = React.useCallback(() => {
    if (onFlowChange) {
      onFlowChange({ nodes, edges });
    }
  }, [onFlowChange, nodes, edges]);

  // Listen for external save trigger
  React.useEffect(() => {
    const handleExternalSave = () => handleSaveFlow();
    document.addEventListener('saveFlow', handleExternalSave);
    return () => document.removeEventListener('saveFlow', handleExternalSave);
  }, [handleSaveFlow]);

  return (
    <div className="flowBuilderCanvas">
      <div className={`flowCanvasWrapper ${editingNode ? 'with-panel' : ''}`}>
        <div className="flowCanvasContainer" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            onNodesChange={isPreviewing ? NOOP : onNodesChange}
            onEdgesChange={isPreviewing ? NOOP : onEdgesChange}
            onConnect={isPreviewing ? NOOP : onConnect}
            onEdgeClick={isPreviewing ? NOOP : onEdgeClick}
            onDrop={isPreviewing ? NOOP : onDrop}
            onDragOver={isPreviewing ? NOOP : onDragOver}
            nodesDraggable={!isPreviewing}
            nodesConnectable={!isPreviewing}
            elementsSelectable={!isPreviewing}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={snapGrid}
            defaultEdgeOptions={defaultEdgeOptions}
            deleteKeyCode={isPreviewing ? null : ['Backspace', 'Delete']}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e5e7eb" gap={20} />
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                switch (node.type) {
                  case 'trigger': return '#22c55e';
                  case 'action': return '#3b82f6';
                  case 'split': return '#8b5cf6';
                  case 'delay': return '#f59e0b';
                  case 'condition': return '#ec4899';
                  case 'tag': return '#14b8a6';
                  case 'data_capture': return '#7c3aed';
                  case 'end': return '#6b7280';
                  case 'auto_send_email': return '#0ea5e9';
                  case 'auto_check_reply': return '#8b5cf6';
                  default: return '#9ca3af';
                }
              }}
            />
          </ReactFlow>
          
          <AddNodePanel 
            onAddNode={handleAddNode} 
            customTouchpoints={customTouchpoints}
            flowType={flowType}
            onPreviewTemplate={setPreviewTemplate}
            previewTemplateId={previewTemplate?.id}
          />

          {previewTemplate && (
            <div className="ghostPreviewBar" style={{ '--tpl-color': previewTemplate.color }}>
              <div className="ghostPreviewInfo">
                <span className="ghostPreviewIcon"><i className={`fa ${previewTemplate.icon}`}></i></span>
                <div className="ghostPreviewText">
                  <span className="ghostPreviewName">{previewTemplate.name}</span>
                  <span className="ghostPreviewMeta">
                    Preview · {previewTemplate.stepCount} steps · {previewTemplate.totalDays} days
                  </span>
                </div>
              </div>
              <div className="ghostPreviewActions">
                <button
                  className="ghostPreviewClearBtn"
                  onClick={() => setPreviewTemplate(null)}
                >
                  <i className="fa fa-times"></i> Clear
                </button>
                <button
                  className="ghostPreviewUseBtn"
                  onClick={() => {
                    handleLoadTemplate(previewTemplate);
                    setPreviewTemplate(null);
                  }}
                >
                  <i className="fa fa-check"></i> Use this flow
                </button>
              </div>
            </div>
          )}
        </div>
        
        {editingNode && (
          <EditNodePanel
            node={editingNode}
            onClose={() => setEditingNode(null)}
            onSave={handleSaveNode}
            customTouchpoints={customTouchpoints}
            emailTopics={emailTopics}
            senderEmails={senderEmails}
            followsLogNotes={edges.some(e => e.target === editingNode.id && nodes.find(n => n.id === e.source)?.type === 'log_notes')}
          />
        )}
      </div>
    </div>
  );
};

// ========== WRAPPER COMPONENT WITH PROVIDER ==========
const FlowBuilderCanvas = (props) => {
  return (
    <ReactFlowProvider>
      <FlowBuilderCanvasInner {...props} />
    </ReactFlowProvider>
  );
};

export default FlowBuilderCanvas;
